import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Buildings,
  House,
  Plus,
  Warning,
  CheckCircle,
  Prohibit,
} from '@phosphor-icons/react';

interface MissingAsset {
  buildingId: string;
  buildingAddress: string;
  unitId: string | null;
  assetTypeId: number;
  assetTypeName: string;
  assetCategoryId: number;
  categoryName: string;
}

interface MissingSummary {
  totalMissing: number;
  byType: { typeId: number; typeName: string; categoryName: string; count: number }[];
}

const MissingAssetsTab: React.FC = () => {
  const { session } = useSession();
  const [missing, setMissing] = useState<MissingAsset[]>([]);
  const [summary, setSummary] = useState<MissingSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterTypeId, setFilterTypeId] = useState<number | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [excludeTarget, setExcludeTarget] = useState<MissingAsset | null>(null);
  const [excludeReason, setExcludeReason] = useState('');

  const fetchMissing = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const resp = await fetchWithAuth(`${API_URL}/assets/missing`, { method: 'GET' }, session);
      if (!resp.ok) throw new Error('Fout bij ophalen');
      const data = await resp.json();
      setMissing(data.missing || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => { fetchMissing(); }, [fetchMissing]);

  const handleExclude = (item: MissingAsset) => {
    setExcludeTarget(item);
    setExcludeReason('');
  };

  const confirmExclude = async () => {
    if (!session || !excludeTarget) return;
    const item = excludeTarget;
    try {
      const resp = await fetchWithAuth(`${API_URL}/assets/exclude-from-requirement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assetTypeId: item.assetTypeId,
          buildingId: item.unitId ? undefined : item.buildingId,
          unitId: item.unitId || undefined,
          reason: excludeReason.trim() || undefined,
        }),
      }, session);
      if (!resp.ok) throw new Error('Uitsluiten mislukt');
      toast.success(`${item.unitId || item.buildingId} uitgesloten van ${item.assetTypeName}`);
      setMissing(prev => prev.filter(m =>
        !(m.buildingId === item.buildingId && m.unitId === item.unitId && m.assetTypeId === item.assetTypeId)
      ));
      if (summary) {
        setSummary({
          ...summary,
          totalMissing: summary.totalMissing - 1,
          byType: summary.byType.map(t =>
            t.typeId === item.assetTypeId ? { ...t, count: t.count - 1 } : t
          ).filter(t => t.count > 0),
        });
      }
      setExcludeTarget(null);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleQuickCreate = async (item: MissingAsset) => {
    if (!session) return;
    const key = `${item.buildingId}-${item.unitId}-${item.assetTypeId}`;
    setCreating(key);
    try {
      const resp = await fetchWithAuth(`${API_URL}/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: item.assetTypeName,
          assetCategoryId: item.assetCategoryId,
          assetTypeId: item.assetTypeId,
          buildingId: item.buildingId,
          unitId: item.unitId || undefined,
          status: 'active',
        }),
      }, session);
      if (!resp.ok) throw new Error('Aanmaken mislukt');
      await resp.json();
      toast.success(`${item.assetTypeName} aangemaakt`);
      // Remove from local list
      setMissing(prev => prev.filter(m =>
        !(m.buildingId === item.buildingId && m.unitId === item.unitId && m.assetTypeId === item.assetTypeId)
      ));
      if (summary) {
        setSummary({
          ...summary,
          totalMissing: summary.totalMissing - 1,
          byType: summary.byType.map(t =>
            t.typeId === item.assetTypeId ? { ...t, count: t.count - 1 } : t
          ).filter(t => t.count > 0),
        });
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setCreating(null);
    }
  };

  const filtered = filterTypeId
    ? missing.filter(m => m.assetTypeId === filterTypeId)
    : missing;

  // Group by building
  const grouped = new Map<string, { address: string; items: MissingAsset[] }>();
  for (const m of filtered) {
    if (!grouped.has(m.buildingId)) {
      grouped.set(m.buildingId, { address: m.buildingAddress, items: [] });
    }
    grouped.get(m.buildingId)!.items.push(m);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (!summary || summary.totalMissing === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <CheckCircle className="h-12 w-12 text-green-500 mb-3" weight="duotone" />
          <h3 className="text-lg font-semibold mb-1">Alles in orde</h3>
          <p className="text-sm text-muted-foreground">
            Er zijn geen ontbrekende verplichte voorzieningen gevonden.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Configureer verplichte asset types in Categorieën &amp; Types instellingen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-600">
          <Warning className="h-5 w-5" weight="fill" />
          <span className="font-semibold text-sm">{summary.totalMissing} ontbrekende voorzieningen</span>
        </div>

        {/* Type filter chips */}
        <button
          onClick={() => setFilterTypeId(null)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            !filterTypeId ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-accent'
          }`}
        >
          Alle ({summary.totalMissing})
        </button>
        {summary.byType.map(t => (
          <button
            key={t.typeId}
            onClick={() => setFilterTypeId(filterTypeId === t.typeId ? null : t.typeId)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              filterTypeId === t.typeId ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-accent'
            }`}
          >
            {t.typeName} ({t.count})
          </button>
        ))}
      </div>

      {/* Grouped by building */}
      <div className="space-y-3">
        {Array.from(grouped.entries()).map(([buildingId, group]) => (
          <Card key={buildingId}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Buildings className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">{buildingId}</span>
                <span className="text-xs text-muted-foreground truncate">{group.address}</span>
                <Badge variant="secondary" className="ml-auto text-xs">{group.items.length} ontbrekend</Badge>
              </div>
              <div className="space-y-1.5">
                {group.items.map(item => {
                  const key = `${item.buildingId}-${item.unitId}-${item.assetTypeId}`;
                  return (
                    <div key={key} className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                      {item.unitId ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-[160px]">
                          <House className="h-3.5 w-3.5" />
                          <span>{item.unitId}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-[160px]">
                          <Buildings className="h-3.5 w-3.5" />
                          <span>Gebouw-niveau</span>
                        </div>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {item.categoryName} &rsaquo; {item.assetTypeName}
                      </Badge>
                      <div className="ml-auto flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1 text-muted-foreground"
                          onClick={() => handleExclude(item)}
                        >
                          Uitsluiten
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs gap-1"
                          disabled={creating === key}
                          onClick={() => handleQuickCreate(item)}
                        >
                          <Plus className="h-3 w-3" />
                          {creating === key ? 'Bezig...' : 'Aanmaken'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Exclude dialog */}
      <Dialog open={excludeTarget !== null} onOpenChange={(open) => !open && setExcludeTarget(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Prohibit className="h-5 w-5 text-red-500" />
              Uitsluiten van verplichting
            </DialogTitle>
          </DialogHeader>
          {excludeTarget && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  {excludeTarget.unitId ? (
                    <><House className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{excludeTarget.unitId}</span></>
                  ) : (
                    <><Buildings className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{excludeTarget.buildingId}</span></>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  wordt uitgesloten van de verplichting voor <span className="font-medium">{excludeTarget.categoryName} &rsaquo; {excludeTarget.assetTypeName}</span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="exclude-reason">Reden (optioneel)</Label>
                <Input
                  id="exclude-reason"
                  value={excludeReason}
                  onChange={(e) => setExcludeReason(e.target.value)}
                  placeholder="Bijv. Geen gasaansluiting in dit gebouw"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  De reden is zichtbaar in de instellingen bij het asset type.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcludeTarget(null)}>Annuleren</Button>
            <Button variant="destructive" onClick={confirmExclude}>Uitsluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissingAssetsTab;
